import { useState } from "react";
import { AlertTriangle, TrendingDown, Users, Database, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

interface CriticalFindingItem {
  id: string;
  name: string;
  value?: number;
  assignedTo?: string;
  lastContact?: string;
  link?: string;
}

interface CriticalFinding {
  type: string;
  severity: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  value: number;
  actionable: string;
  items?: CriticalFindingItem[];
}

interface CriticalFindingsAlertProps {
  findings: CriticalFinding[];
}

export const CriticalFindingsAlert = ({ findings }: CriticalFindingsAlertProps) => {
  const [expandedFindings, setExpandedFindings] = useState<Set<number>>(new Set());
  const navigate = useNavigate();

  if (findings.length === 0) return null;

  const highSeverity = findings.filter(f => f.severity === 'high');
  const mediumSeverity = findings.filter(f => f.severity === 'medium');

  const getIcon = (type: string) => {
    switch (type) {
      case 'user_performance':
      case 'lead_follow_up':
        return Users;
      case 'stale_leads':
      case 'lost_revenue':
        return TrendingDown;
      case 'data_completeness':
        return Database;
      default:
        return AlertTriangle;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const toggleExpand = (index: number) => {
    const newExpanded = new Set(expandedFindings);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedFindings(newExpanded);
  };

  const handleItemClick = (link: string | undefined) => {
    if (link) {
      navigate(link);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Critical Findings</h3>
        <div className="flex gap-2">
          {highSeverity.length > 0 && (
            <Badge variant="destructive">{highSeverity.length} High Priority</Badge>
          )}
          {mediumSeverity.length > 0 && (
            <Badge variant="default">{mediumSeverity.length} Medium Priority</Badge>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {findings.map((finding, index) => {
          const Icon = getIcon(finding.type);
          const isExpanded = expandedFindings.has(index);
          const hasItems = finding.items && finding.items.length > 0;
          const displayedItems = isExpanded ? finding.items : finding.items?.slice(0, 3);

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Alert variant={finding.severity === 'high' ? 'destructive' : 'default'}>
                <Icon className="h-4 w-4" />
                <AlertTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {finding.title}
                    <Badge variant={getSeverityColor(finding.severity)}>
                      {finding.severity}
                    </Badge>
                  </div>
                  {hasItems && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpand(index)}
                      className="h-6 px-2"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </AlertTitle>
                <AlertDescription className="mt-2 space-y-3">
                  <p>{finding.description}</p>

                  {/* Items List */}
                  {hasItems && (
                    <AnimatePresence>
                      <motion.div
                        initial={false}
                        animate={{ height: "auto" }}
                        className="space-y-2 border-l-2 border-primary/30 pl-4 mt-3"
                      >
                        {displayedItems?.map((item, itemIdx) => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: itemIdx * 0.05 }}
                            onClick={() => handleItemClick(item.link)}
                            className={`p-2 rounded-md bg-background/50 text-sm ${
                              item.link ? 'cursor-pointer hover:bg-accent transition-colors' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 space-y-1">
                                <div className="font-medium flex items-center gap-2">
                                  {item.name}
                                  {item.value && (
                                    <span className="text-primary font-bold">
                                      ${item.value.toLocaleString()}
                                    </span>
                                  )}
                                  {item.link && (
                                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground flex gap-4">
                                  {item.assignedTo && (
                                    <span>Assigned: {item.assignedTo}</span>
                                  )}
                                  {item.lastContact && (
                                    <span>Last contact: {item.lastContact}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}

                        {!isExpanded && finding.items && finding.items.length > 3 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpand(index)}
                            className="w-full text-xs"
                          >
                            +{finding.items.length - 3} more...
                          </Button>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  )}

                  <p className="text-sm font-medium border-l-2 border-primary pl-3 mt-3">
                    💡 {finding.actionable}
                  </p>
                </AlertDescription>
              </Alert>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
